import clsx from 'clsx';
import type { ToolName } from '@arcanum-hq/shared-types';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import {
  formatCurrency,
  formatPercent,
  formatMultiple,
  formatNumber,
} from '@/lib/format';
import { PhaseShell, MetricSlotSkeleton } from './ToolResultRendererSkeleton';

interface MetricsCardProps {
  state: ToolCallState;
}

// --- Type guards ---

interface SetInputItem {
  name: string;
  value: unknown;
  previous: unknown;
}

interface SetInputData {
  set: SetInputItem[];
  errors: string[];
  currentMetrics: Record<string, unknown>;
  warnings: string[];
}

function isSetInputResult(data: unknown): data is SetInputData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'set' in data &&
    Array.isArray((data as SetInputData).set)
  );
}

interface CalculateOutput {
  name: string;
  displayName: string;
  value: unknown;
  displayFormat: string;
}

interface CalculateData {
  summary: {
    inputsSet: number;
    inputsTotal: number;
    inputsRemaining: number;
    requiredMissing: string[];
  };
  inputs: Array<{
    name: string;
    displayName: string;
    value: unknown;
    isDefault: boolean;
    displayFormat: string;
  }>;
  outputs: CalculateOutput[];
  validationIssues: Array<{ field: string; severity: string; message: string }>;
  readyForGeneration: boolean;
}

function isCalculateResult(data: unknown): data is CalculateData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'summary' in data &&
    'outputs' in data
  );
}

interface GetValuesData {
  values: Record<
    string,
    { value: unknown; displayName: string; formatted: string }
  >;
  errors: string[];
}

function isGetValuesResult(data: unknown): data is GetValuesData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'values' in data &&
    typeof (data as GetValuesData).values === 'object'
  );
}

interface ValidateInputData {
  valid: boolean;
  normalizedValue?: unknown;
  displayFormatted?: string;
  issues: Array<{
    ruleId: string;
    severity: string;
    message: string;
    suggestedFix?: string;
  }>;
  typicalRange?: string;
  benchmarkSource?: string;
}

function isValidateInputResult(data: unknown): data is ValidateInputData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'valid' in data &&
    'issues' in data
  );
}

// `GenerateExcelData` / `isGenerateExcelResult` / `renderGenerateExcel`
// REMOVED 2026-05-20 — Excel download deprecated platform-wide. The
// Model tab is the canonical model view. Historical tool-result envelopes
// fall through to the generic renderer.

interface ExportSensitivityTabData {
  url: string;
  tabName: string;
}

function isExportSensitivityTabResult(
  data: unknown
): data is ExportSensitivityTabData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'tabName' in data &&
    'url' in data
  );
}

const KEY_OUTPUT_NAMES = [
  'IRR',
  'EquityMultiple',
  'StabilizedDSCR',
  'StabilizedCoC',
  'GrossPotentialRent',
  'DebtPrincipal',
  'RentableSF',
  'WARent',
];

function renderSetInputs(data: SetInputData) {
  // Defensive: see renderGetValues note — revisit-mode tool_results may
  // be missing optional array fields. `?? []` is the canonical guard.
  const setItems = data.set ?? [];
  const errors = data.errors ?? [];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-mono font-semibold text-accent tracking-[1px]">
          SET
        </span>
        {setItems.map((s) => (
          <span
            key={s.name}
            className="text-xs font-mono text-text-muted bg-input-bg px-2 py-0.5 rounded-[2px] border border-panel-border"
          >
            {s.name}
          </span>
        ))}
      </div>
      {errors.length > 0 &&
        errors.map((e, i) => (
          <div key={i} className="text-xs font-mono text-danger">
            {e}
          </div>
        ))}
    </div>
  );
}

function renderCalculate(data: CalculateData) {
  // Defensive: see renderGetValues note. CalculateData is hydrated from
  // persisted tool_results on revisit; optional arrays may be missing.
  const outputs = data.outputs ?? [];
  const validationIssues = data.validationIssues ?? [];
  const summary = data.summary ?? { inputsSet: 0, inputsTotal: 0 };
  const keyOutputs = outputs.filter((o) => KEY_OUTPUT_NAMES.includes(o.name));
  const otherCount = outputs.length - keyOutputs.length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono font-semibold text-accent tracking-[1px]">
          MODEL SNAPSHOT
        </span>
        <span className="text-xs font-mono text-text-dim">
          {summary.inputsSet}/{summary.inputsTotal} inputs
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {keyOutputs.map((o) => (
          <div
            key={o.name}
            className="bg-bg border border-panel-border rounded-[2px] px-2 py-1.5"
          >
            <div className="text-micro font-mono text-text-dim uppercase tracking-[0.8px]">
              {o.displayName}
            </div>
            <div className="text-sm font-mono text-text-bright tabular-nums font-medium">
              {formatOutputValue(o)}
            </div>
          </div>
        ))}
      </div>
      {otherCount > 0 && (
        <div className="text-micro font-mono text-text-dim">
          + {otherCount} more outputs
        </div>
      )}
      {validationIssues.length > 0 &&
        validationIssues.map((issue, i) => (
          <div
            key={i}
            className={clsx(
              'text-xs font-mono',
              issue.severity === 'error' ? 'text-danger' : 'text-warn'
            )}
          >
            {issue.field}: {issue.message}
          </div>
        ))}
    </div>
  );
}

function formatOutputValue(output: {
  value: unknown;
  displayFormat: string;
}): string {
  if (typeof output.value !== 'number') return String(output.value ?? '-');
  switch (output.displayFormat) {
    case 'percent':
      return formatPercent(output.value);
    case 'currency':
      return formatCurrency(output.value);
    case 'multiple':
      return formatMultiple(output.value);
    default:
      return formatNumber(output.value, 2);
  }
}

function renderGetValues(data: GetValuesData) {
  const entries = Object.entries(data.values ?? {});
  // Defensive: revisit-mode hydration may persist `tool_results` without
  // `errors` (the auth-side agent always emits the full shape, but cold-load
  // shape drift can drop optional fields). `?? []` keeps the renderer crash-
  // free regardless. Mirrors the defensive-default sweep across the 5 other
  // tool-result renderers below.
  const errors = data.errors ?? [];
  return (
    <div className="space-y-2">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Values
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {entries.map(([key, info]) => (
          <div
            key={key}
            className="bg-bg border border-panel-border rounded-[2px] px-2 py-1.5"
          >
            <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">
              {info.displayName}
            </div>
            <div className="text-sm font-mono text-text-bright tabular-nums">
              {info.formatted}
            </div>
          </div>
        ))}
      </div>
      {errors.length > 0 && (
        <div className="space-y-0.5">
          {errors.map((e, i) => (
            <div key={i} className="text-xs font-mono text-danger">
              {e}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderValidateInput(data: ValidateInputData) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div
          className={clsx(
            'text-xs font-mono font-semibold tracking-[1.2px] uppercase',
            data.valid ? 'text-accent' : 'text-danger'
          )}
        >
          {data.valid ? 'Validation Passed' : 'Validation Failed'}
        </div>
        <div
          className={clsx(
            'w-1.5 h-1.5 rounded-full',
            data.valid ? 'bg-accent' : 'bg-danger'
          )}
        />
      </div>
      {(data.issues ?? []).length > 0 && (
        <div className="space-y-0.5">
          {(data.issues ?? []).map((issue, i) => (
            <div
              key={i}
              className={clsx(
                'text-xs font-mono',
                issue.severity === 'error' ? 'text-danger' : 'text-warn'
              )}
            >
              {issue.message}
              {issue.suggestedFix && (
                <span className="text-text-muted ml-1">
                  ({issue.suggestedFix})
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {data.typicalRange && (
        <div className="text-xs font-mono text-text-muted">
          Typical range: {data.typicalRange}
        </div>
      )}
    </div>
  );
}

// `renderGenerateExcel` REMOVED 2026-05-20 — generate_excel tool no longer
// exists. The MetricsCard envelope's `renderComplete` dispatcher no longer
// has a 'generate_excel' arm; historical persisted tool-result envelopes
// fall through the dispatcher and render via the generic envelope. The
// downstream `GenerateExcelData` interface + `isGenerateExcelResult` guard
// were removed at the top of this file.

function renderExportSensitivityTab(data: ExportSensitivityTabData) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Sensitivity Tab Exported
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-mono text-text-bright">
          {data.tabName}
        </span>
        <a
          href={data.url}
          download
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-accent-dim border border-accent/20 rounded-[2px] text-sm font-mono text-accent hover:bg-accent/10 transition-colors"
        >
          Download
        </a>
      </div>
    </div>
  );
}

function renderComplete(tool: ToolName, result: unknown) {
  switch (tool) {
    case 'set_inputs':
      if (isSetInputResult(result)) return renderSetInputs(result);
      break;
    case 'calculate':
      if (isCalculateResult(result)) return renderCalculate(result);
      break;
    case 'get_values':
      if (isGetValuesResult(result)) return renderGetValues(result);
      break;
    case 'validate_input':
      if (isValidateInputResult(result)) return renderValidateInput(result);
      break;
    // `generate_excel` REMOVED 2026-05-20 — tool no longer dispatched.
    case 'export_sensitivity_tab':
      if (isExportSensitivityTabResult(result))
        return renderExportSensitivityTab(result);
      break;
  }
  return (
    <div className="text-xs font-mono text-text-muted">
      <pre className="whitespace-pre-wrap">
        {JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

const KEY_METRIC_SLOTS = ['IRR', 'DSCR', 'EM', 'CoC'];

/**
 * Phase 4 Session 2 — `market_data_enrichment` 3-tier ticker.
 * Renders tier rows from `state.progress` (filtered to tier_*).
 * Dedupes by `tier` — latest event per tier wins.
 */
function renderTierTicker(
  state: Extract<ToolCallState, { phase: 'executing' }>
) {
  const tierMap = new Map<
    string,
    {
      phase: 'tier_start' | 'tier_hit' | 'tier_skip';
      source?: string;
      reason?: string;
    }
  >();
  for (const evt of state.progress) {
    if (
      evt.phase !== 'tier_start' &&
      evt.phase !== 'tier_hit' &&
      evt.phase !== 'tier_skip'
    )
      continue;
    const payload = evt.payload as {
      tier?: string;
      source?: string;
      reason?: string;
    };
    if (typeof payload.tier !== 'string') continue;
    tierMap.set(payload.tier, {
      phase: evt.phase,
      source: payload.source,
      reason: payload.reason,
    });
  }
  if (tierMap.size === 0) {
    return (
      <div className="text-xs font-mono text-text-muted">
        Resolving market sources…
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="market-tier-ticker">
      {Array.from(tierMap.entries()).map(([tier, info]) => (
        <div
          key={tier}
          data-tier={tier}
          data-tier-phase={info.phase}
          className="flex items-center gap-2 text-xs font-mono"
        >
          <span
            className={clsx(
              'w-3 inline-block text-center',
              info.phase === 'tier_hit' && 'text-accent',
              info.phase === 'tier_skip' && 'text-text-dim',
              info.phase === 'tier_start' && 'text-warn'
            )}
          >
            {info.phase === 'tier_hit'
              ? '✓'
              : info.phase === 'tier_skip'
              ? '✗'
              : '◇'}
          </span>
          <span className="text-text-muted uppercase tracking-[1px]">
            {tier}
          </span>
          {info.source && (
            <span className="text-text-dim">→ {info.source}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Phase 4 Session 2 — `apply_market_defaults` lookup state + applied count.
 */
function renderApplyDefaultsProgress(
  state: Extract<ToolCallState, { phase: 'executing' }>
) {
  const lookupEvent = state.progress.find((p) => p.phase === 'lookup_start');
  const appliedEvent = state.progress.find(
    (p) => p.phase === 'defaults_applied'
  );
  const lookupPayload = lookupEvent?.payload as
    | { deal_type?: string; location?: string }
    | undefined;
  const appliedPayload = appliedEvent?.payload as
    | { count?: number }
    | undefined;

  return (
    <div
      className="text-xs font-mono space-y-1"
      data-testid="apply-defaults-progress"
    >
      {lookupPayload && (
        <div className="text-text-muted">
          Looking up defaults for {lookupPayload.deal_type ?? '…'}
          {lookupPayload.location ? `, ${lookupPayload.location}` : ''}…
        </div>
      )}
      {appliedPayload && typeof appliedPayload.count === 'number' && (
        <div className="text-accent">
          Applied {appliedPayload.count} defaults
        </div>
      )}
      {!lookupPayload && !appliedPayload && (
        <div className="text-text-muted">Applying market defaults…</div>
      )}
    </div>
  );
}

/**
 * Phase-aware MetricsCard. Per directive §7.1:
 * - skeleton (input_streaming / input_ready): greyed labels for known metric
 *   slots; title "Calculating from current model…"
 * - in-progress (executing): same skeleton + subtle pulsing accent border
 * - complete: existing 7-tool switch (set_inputs / calculate / get_values /
 *   validate_input / generate_excel / export_sensitivity_tab / set_table_inputs)
 * - aborted: skeleton + "Aborted" badge
 *
 * Phase 4 Session 2 adds two tool-specific executing branches:
 * - `market_data_enrichment` → 3-tier ticker via `state.progress`.
 * - `apply_market_defaults` → lookup_start + defaults_applied lines.
 *
 * `errored` is intercepted at the dispatcher level — never reaches this file.
 */
export function MetricsCard({ state }: MetricsCardProps) {
  if (state.phase === 'complete') {
    return <>{renderComplete(state.tool, state.result)}</>;
  }

  if (state.phase === 'aborted') {
    return (
      <PhaseShell variant="aborted" title="Calculation interrupted">
        <div className="grid grid-cols-2 gap-1.5">
          {KEY_METRIC_SLOTS.map((label) => (
            <MetricSlotSkeleton key={label} label={label} />
          ))}
        </div>
      </PhaseShell>
    );
  }

  // Phase 4 Session 2 — tool-specific executing-phase renderers.
  if (state.phase === 'executing' && state.tool === 'market_data_enrichment') {
    return (
      <PhaseShell variant="in-progress" title="Fetching market data…">
        {renderTierTicker(state)}
      </PhaseShell>
    );
  }
  if (state.phase === 'executing' && state.tool === 'apply_market_defaults') {
    return (
      <PhaseShell variant="in-progress" title="Applying market defaults…">
        {renderApplyDefaultsProgress(state)}
      </PhaseShell>
    );
  }

  // input_streaming / input_ready / executing → skeleton with phase-specific shell.
  const variant = state.phase === 'executing' ? 'in-progress' : 'skeleton';
  return (
    <PhaseShell variant={variant} title="Calculating from current model…">
      <div className="grid grid-cols-2 gap-1.5">
        {KEY_METRIC_SLOTS.map((label) => (
          <MetricSlotSkeleton key={label} label={label} />
        ))}
      </div>
    </PhaseShell>
  );
}
